import { AutomationContentType } from 'custom-type';
import { ZapGoalId, ZapGoalQuestionId } from '../zapGoalTemplates';
import imgGoal from '../../../../../../assets/images/zap-tour/screens/02-goal.jpeg';
import imgGrowth from '../../../../../../assets/images/zap-tour/screens/04-growth-followers.jpeg';
import imgPosts from '../../../../../../assets/images/zap-tour/screens/07-posts-selected.jpeg';
import imgWord from '../../../../../../assets/images/zap-tour/screens/08-word.jpeg';
import imgReply from '../../../../../../assets/images/zap-tour/screens/09-reply.jpeg';
import imgWho from '../../../../../../assets/images/zap-tour/screens/10-who-followers.jpeg';
import imgCard from '../../../../../../assets/images/zap-tour/screens/12-card.jpeg';
import imgMessage from '../../../../../../assets/images/zap-tour/screens/13-message-link.jpeg';
import imgReview from '../../../../../../assets/images/zap-tour/screens/14-review.jpeg';

/* In-wizard help: the ⓘ next to "Step X of 4" opens a short sheet for the
   screen the creator is on — an example screenshot, a few plain lines and one
   tip. Written for a first-timer, like the "How to use Zap" guide it links to,
   and reusing that guide's screenshots. */

/** The screen the wizard is showing, as far as help is concerned. */
export type StepHelpScreen =
  | 'goal'
  | 'growth'
  | 'posts'
  | ZapGoalQuestionId
  // The full rule form ("Change advanced settings", or a Zap the guided
  // questions could not read back).
  | 'rules';

export interface StepHelpContent {
  title: string;
  image?: string;
  points: string[];
  tip?: string;
}

const IMG = {
  goal: imgGoal,
  growth: imgGrowth,
  posts: imgPosts,
  word: imgWord,
  reply: imgReply,
  who: imgWho,
  card: imgCard,
  message: imgMessage,
  review: imgReview,
};

interface StepHelpContext {
  contentType: AutomationContentType;
  goal: ZapGoalId | null;
  /** Editing shows a three-way "Who" question instead of the two-way one. */
  editing: boolean;
}

export const getStepHelp = (screen: StepHelpScreen, ctx: StepHelpContext): StepHelpContent => {
  const stories = ctx.contentType === 'story';
  const item = stories ? 'story' : 'post';
  const items = stories ? 'stories' : 'posts';

  switch (screen) {
    case 'goal':
      return {
        title: 'Choose what you want',
        image: IMG.goal,
        points: [
          'Share my link — send people a link.',
          'Earn affiliate revenue — send products people can buy.',
          'Answer a common question — reply with an answer.',
          'Collect leads — get people’s contact details.',
        ],
        tip: 'Not sure? Pick Share my link. Cards with an Upgrade badge need a paid plan.',
      };
    case 'growth':
      return {
        title: 'Want more followers?',
        image: IMG.growth,
        points: [
          'Yes — people are asked to follow you before they get your message.',
          'No — everyone who comments gets your message.',
        ],
        tip: 'Pick Yes if growing your followers matters to you.',
      };
    case 'posts':
      return {
        title: `Choose your ${items}`,
        image: stories ? undefined : IMG.posts,
        points: [
          `Tick the ${items} Zap should watch, then click Next.`,
          `Any ${stories ? 'Story' : 'Post'} — use it on all your ${items}, including new ones.`,
          `Click × on a thumbnail to remove a ${item}, or Clear all to start over.`,
        ],
        tip: `Click View on Instagram to double-check a ${item}.`,
      };
    case 'keywords':
      return {
        title: 'The word people comment',
        image: IMG.word,
        points: [
          'Pick a word like LINK, or type your own and click +.',
          `When someone comments that word on your ${item}, your Zap starts.`,
          'The box below shows what they will see.',
        ],
        tip: 'Short, easy words work best.',
      };
    case 'replies':
      return {
        title: 'A reply under their comment',
        image: IMG.reply,
        points: [
          'Everyone can see this reply on your post.',
          'Add a few and Zap picks one at random each time.',
          'Leave it empty to reply in their DMs only.',
        ],
        tip: 'Click a suggestion like “Check your inbox 😊” to use it.',
      };
    case 'follow_gate':
      return {
        title: 'Who gets your message',
        image: ctx.editing ? undefined : IMG.who,
        points: ctx.editing
          ? [
              'Everyone who comments — anyone gets it, follower or not.',
              'Ask them to follow — we ask first, but send it either way.',
              'Only if they follow — they follow you, then it is sent.',
            ]
          : [
              'Only people who follow me — we ask them to follow, then send it.',
              'Everyone who asks — we still ask them to follow, but send it either way.',
            ],
        tip: 'Only people who follow me is best for growing.',
      };
    case 'opener':
      return {
        title: 'The “please follow me” card',
        image: IMG.card,
        points: [
          'This card arrives first, with a button.',
          'When they tap the button, your message is sent.',
          'Edit the words and the button text (up to 20 characters), or pick a suggestion.',
        ],
        tip: 'The words already filled in work fine — you can just click Next.',
      };
    case 'delivery':
      return ctx.goal === 'link'
        ? {
            title: 'Your message and link',
            image: IMG.message,
            points: [
              'Write a short message, or pick a suggestion.',
              'Paste the link you want to share.',
              'They get it as a button they can tap.',
            ],
            tip: 'Check the link opens the right page before you save.',
          }
        : {
            title: 'The message they get',
            points: [
              'Write what lands in their inbox when they comment.',
              'Add a link and it shows as a button they can tap.',
            ],
            tip: 'Keep it short and friendly.',
          };
    case 'products':
      return {
        title: 'The product you’re sharing',
        points: [
          'Paste the product’s link.',
          'Zap shows its picture and price for you.',
          'They get it as a product card they can tap to buy.',
        ],
        tip: 'Use your affiliate link so you earn from each sale.',
      };
    case 'signup':
      return {
        title: 'Where they leave their details',
        points: [
          'Already have a form (like a Google Form)? Paste its link.',
          'No form? Build one right here — pick what to ask them.',
          'Their details come straight to you.',
        ],
      };
    case 'review':
      return {
        title: 'Check it and create',
        image: IMG.review,
        points: [
          'This is a short summary of what will happen.',
          'Click any line to change it.',
          'Click + Add to answer a different word with its own message.',
          `Click ${ctx.editing ? 'Update' : 'Create'} Automation when you’re happy.`,
        ],
        tip: 'You can turn it off or change it later from My Zaps.',
      };
    case 'rules':
    default:
      return {
        title: 'Set up your rules',
        points: [
          'Add the words people comment (click + after each).',
          'Add an optional reply under their comment.',
          'Pick Direct Message or Zap Link and fill it in, then click Add Rule.',
          `Click ${ctx.editing ? 'Update' : 'Create'} Automation when you’re done.`,
        ],
        tip: 'Tip: the guided questions are the easiest way — you only need this for extra control.',
      };
  }
};
